import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, decimalStringSchema, httpUrlSchema, isoDateTimeSchema, permissionsSchema, positiveDecimalStringSchema, uuidSchema, queryBooleanSchema, } from './common.js';
import { allergenSchema, dietaryLabelSchema, difficultySchema, labelKindSchema, labelSourceSchema, recipeOriginSchema, recipeStatusSchema, recipeVisibilitySchema, sourceTypeSchema, measurementSystemSchema, } from './enums.js';
import { publicUserSchema } from './users.js';
// ---------------------------------------------------------------------------
// Write model
// ---------------------------------------------------------------------------
/** How many base recipes one step can link to. */
export const MAX_STEP_RECIPE_LINKS = 10;
export const recipeIngredientInputSchema = z
    .object({
    id: uuidSchema.optional(),
    quantity: decimalStringSchema.nullable().optional(),
    quantityMax: decimalStringSchema.nullable().optional(),
    /** Free-text fallback such as "to taste", "a pinch". */
    quantityText: z.string().trim().max(80).nullable().optional(),
    unitCode: z.string().trim().max(32).nullable().optional(),
    /** Unit text that could not be normalized to the catalog. */
    unitText: z.string().trim().max(40).nullable().optional(),
    name: z.string().trim().min(1).max(200),
    note: z.string().trim().max(200).nullable().optional(),
    optional: z.boolean().default(false),
    scalable: z.boolean().default(true),
    /** Text exactly as imported or dictated, kept for audit. */
    originalText: z.string().max(500).nullable().optional(),
    /** The base recipe this line stands for ("200 ml vegetable stock"); the text stays as written. */
    linkedRecipeId: uuidSchema.nullable().optional(),
})
    .refine((v) => !(v.quantity && v.quantityMax && Number(v.quantityMax) < Number(v.quantity)), {
    message: 'quantityMax must be greater than or equal to quantity',
    path: ['quantityMax'],
})
    .refine((v) => !v.quantity || !v.quantity.startsWith('-'), {
    message: 'Quantity cannot be negative',
    path: ['quantity'],
});
export const recipeIngredientSectionInputSchema = z.object({
    id: uuidSchema.optional(),
    title: z.string().trim().max(120).nullable(),
    ingredients: z.array(recipeIngredientInputSchema).max(200),
});
export const recipeStepInputSchema = z.object({
    id: uuidSchema.optional(),
    instruction: z.string().trim().min(1).max(5000),
    durationSeconds: z
        .number()
        .int()
        .min(0)
        .max(60 * 60 * 24 * 7)
        .nullable()
        .optional(),
    /** Ingredient ids (or client keys for new recipes) referenced by this step. */
    ingredientRefs: z.array(z.string().max(64)).max(50).optional(),
    /** Base recipes this step uses. */
    linkedRecipeIds: z.array(uuidSchema).max(MAX_STEP_RECIPE_LINKS).optional(),
    imageAssetId: uuidSchema.nullable().optional(),
});
export const recipeStepSectionInputSchema = z.object({
    id: uuidSchema.optional(),
    title: z.string().trim().max(120).nullable(),
    steps: z.array(recipeStepInputSchema).max(200),
});
export const recipeLabelInputSchema = z.object({
    kind: labelKindSchema,
    value: z.string().min(1).max(40),
    source: labelSourceSchema.default('USER'),
    confirmed: z.boolean().default(true),
});
export const recipeWriteInputSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullable().optional(),
    servings: positiveDecimalStringSchema,
    /** e.g. "servings", "pieces", "loaf" */
    servingsLabel: z.string().trim().max(40).nullable().optional(),
    prepTimeMinutes: z.number().int().min(0).max(10080).nullable().optional(),
    cookTimeMinutes: z.number().int().min(0).max(10080).nullable().optional(),
    restTimeMinutes: z.number().int().min(0).max(10080).nullable().optional(),
    difficulty: difficultySchema.nullable().optional(),
    cuisine: z.string().trim().max(80).nullable().optional(),
    mealType: z.string().trim().max(40).nullable().optional(),
    notes: z.string().trim().max(10000).nullable().optional(),
    /** A video of the dish: a YouTube link (embedded) or any http(s) link (shown as a link). */
    videoUrl: httpUrlSchema.nullable().optional(),
    /** A building block (stock, dough, sauce) other recipes link to; left alone when not sent. */
    isBaseRecipe: z.boolean().optional(),
    /** Language the recipe is written in; the server takes the author's when it is left out. */
    language: z.string().trim().min(2).max(10).nullable().optional(),
    /** Measurement system it is written in; guessed from the units when left out. */
    unitSystem: measurementSystemSchema.nullable().optional(),
    status: recipeStatusSchema.default('DRAFT'),
    visibility: recipeVisibilitySchema.default('PRIVATE'),
    householdId: uuidSchema.nullable().optional(),
    coverAssetId: uuidSchema.nullable().optional(),
    galleryAssetIds: z.array(uuidSchema).max(30).optional(),
    ingredientSections: z.array(recipeIngredientSectionInputSchema).max(30).default([]),
    stepSections: z.array(recipeStepSectionInputSchema).max(30).default([]),
    tags: z.array(z.string().trim().min(1).max(50)).max(50).default([]),
    categoryIds: z.array(uuidSchema).max(20).default([]),
    labels: z.array(recipeLabelInputSchema).max(40).default([]),
    /** When saving a reviewed AI draft: the generation it came from (must belong to the actor). */
    aiGenerationId: uuidSchema.nullable().optional(),
});
export const recipeUpdateInputSchema = recipeWriteInputSchema.partial().extend({
    /** Optimistic concurrency: the version the client last saw. */
    version: z.number().int().nonnegative(),
    revisionReason: z.string().max(200).optional(),
});
// ---------------------------------------------------------------------------
// Read model
// ---------------------------------------------------------------------------
/** A recipe another one links to – only ever one the viewer may open. */
export const recipeLinkSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    coverThumbnailUrl: z.string().nullable(),
    isBaseRecipe: z.boolean(),
});
export const recipeIngredientSchema = z.object({
    id: uuidSchema,
    quantity: decimalStringSchema.nullable(),
    quantityMax: decimalStringSchema.nullable(),
    quantityText: z.string().nullable(),
    unitCode: z.string().nullable(),
    unitText: z.string().nullable(),
    name: z.string(),
    note: z.string().nullable(),
    optional: z.boolean(),
    scalable: z.boolean(),
    originalText: z.string().nullable(),
    position: z.number().int(),
    /** The base recipe behind this line; null when there is none or the viewer may not see it. */
    linkedRecipe: recipeLinkSchema.nullable().optional(),
});
export const recipeIngredientSectionSchema = z.object({
    id: uuidSchema,
    title: z.string().nullable(),
    position: z.number().int(),
    ingredients: z.array(recipeIngredientSchema),
});
export const recipeStepSchema = z.object({
    id: uuidSchema,
    instruction: z.string(),
    durationSeconds: z.number().int().nullable(),
    ingredientRefs: z.array(uuidSchema),
    /** Base recipes this step uses, as far as the viewer may see them. */
    linkedRecipes: z.array(recipeLinkSchema).optional(),
    imageAssetId: uuidSchema.nullable(),
    imageUrl: z.string().nullable(),
    position: z.number().int(),
});
export const recipeStepSectionSchema = z.object({
    id: uuidSchema,
    title: z.string().nullable(),
    position: z.number().int(),
    steps: z.array(recipeStepSchema),
});
export const recipeLabelSchema = recipeLabelInputSchema.extend({ id: uuidSchema });
export const recipeMediaSchema = z.object({
    assetId: uuidSchema,
    role: z.enum(['COVER', 'GALLERY', 'STEP']),
    altText: z.string().nullable(),
    position: z.number().int(),
    url: z.string(),
    thumbnailUrl: z.string().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    /** `#rrggbb`; tints the recipe header (appearance spec 6.4). */
    dominantColor: z.string().nullable().optional(),
});
export const recipeSourceSchema = z.object({
    id: uuidSchema,
    type: sourceTypeSchema,
    url: z.string().nullable(),
    canonicalUrl: z.string().nullable(),
    domain: z.string().nullable(),
    siteName: z.string().nullable(),
    author: z.string().nullable(),
    originalTitle: z.string().nullable(),
    attribution: z.string().nullable(),
    fingerprint: z.string().nullable(),
    importedAt: isoDateTimeSchema.nullable(),
    importerVersion: z.string().nullable(),
    originalImageUrl: z.string().nullable(),
    aiGenerationId: uuidSchema.nullable(),
});
export const tagRefSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    /** One of the ten category hues; absent or null = derived from the name. */
    color: z.number().int().min(1).max(10).nullable().optional(),
});
export const categoryRefSchema = z.object({
    id: uuidSchema,
    slug: z.string(),
    name: z.string(),
    path: z.string(),
});
export const recipeSchema = z.object({
    id: uuidSchema,
    ownerId: uuidSchema,
    owner: publicUserSchema,
    householdId: uuidSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    servings: decimalStringSchema,
    servingsLabel: z.string().nullable(),
    prepTimeMinutes: z.number().int().nullable(),
    cookTimeMinutes: z.number().int().nullable(),
    restTimeMinutes: z.number().int().nullable(),
    totalTimeMinutes: z.number().int().nullable(),
    difficulty: difficultySchema.nullable(),
    cuisine: z.string().nullable(),
    mealType: z.string().nullable(),
    notes: z.string().nullable(),
    /** A video of the dish; a YouTube link is embedded on the recipe page. */
    videoUrl: z.string().nullable().optional(),
    /** Language of title, ingredients and steps – sets `lang` on the content. */
    language: z.string().nullable().optional(),
    unitSystem: measurementSystemSchema.nullable().optional(),
    origin: recipeOriginSchema,
    status: recipeStatusSchema,
    visibility: recipeVisibilitySchema,
    publicSlug: z.string().nullable(),
    isFavorite: z.boolean(),
    isBaseRecipe: z.boolean().optional(),
    /** Recipes the viewer may see that link to this one. */
    usedIn: z.array(recipeLinkSchema).optional(),
    rating: z.number().min(0).max(5).nullable(),
    cover: recipeMediaSchema.nullable(),
    gallery: z.array(recipeMediaSchema),
    ingredientSections: z.array(recipeIngredientSectionSchema),
    stepSections: z.array(recipeStepSectionSchema),
    tags: z.array(tagRefSchema),
    categories: z.array(categoryRefSchema),
    labels: z.array(recipeLabelSchema),
    source: recipeSourceSchema.nullable(),
    parentRecipeId: uuidSchema.nullable(),
    variationLabel: z.string().nullable(),
    lastCookedAt: isoDateTimeSchema.nullable(),
    cookCount: z.number().int(),
    version: z.number().int(),
    permissions: permissionsSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    deletedAt: isoDateTimeSchema.nullable(),
});
export const recipeSummarySchema = z.object({
    id: uuidSchema,
    title: z.string(),
    description: z.string().nullable(),
    coverThumbnailUrl: z.string().nullable(),
    totalTimeMinutes: z.number().int().nullable(),
    difficulty: difficultySchema.nullable(),
    servings: decimalStringSchema,
    rating: z.number().nullable(),
    isFavorite: z.boolean(),
    isBaseRecipe: z.boolean().optional(),
    origin: recipeOriginSchema,
    status: recipeStatusSchema,
    visibility: recipeVisibilitySchema,
    tags: z.array(tagRefSchema),
    categories: z.array(categoryRefSchema),
    cuisine: z.string().nullable(),
    mealType: z.string().nullable(),
    sourceDomain: z.string().nullable(),
    lastCookedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
});
export const recipeSortSchema = z.enum([
    'relevance',
    'title',
    'rating',
    'lastCooked',
    'recentlyAdded',
    'recentlyUpdated',
    'prepTime',
    'totalTime',
]);
const csv = (item) => z.preprocess((v) => typeof v === 'string'
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : v, z.array(item));
export const recipeListQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().max(200).optional(),
    status: recipeStatusSchema.optional(),
    visibility: recipeVisibilitySchema.optional(),
    categoryIds: csv(uuidSchema).optional(),
    tags: csv(z.string()).optional(),
    cuisine: z.string().max(80).optional(),
    mealType: z.string().max(40).optional(),
    difficulty: difficultySchema.optional(),
    minRating: z.coerce.number().min(0).max(5).optional(),
    favorite: queryBooleanSchema.optional(),
    /** true = base recipes only, false = everything else. */
    baseRecipe: queryBooleanSchema.optional(),
    maxTotalMinutes: z.coerce.number().int().positive().optional(),
    dietary: csv(dietaryLabelSchema).optional(),
    excludeAllergens: csv(allergenSchema).optional(),
    /** Ingredient names available to the user ("cook with what I have"). */
    availableIngredients: csv(z.string()).optional(),
    collectionId: uuidSchema.optional(),
    householdId: uuidSchema.optional(),
    includeArchived: queryBooleanSchema.default(false),
    trashed: queryBooleanSchema.default(false),
    sort: recipeSortSchema.default('recentlyUpdated'),
    order: z.enum(['asc', 'desc']).optional(),
});
export const recipeListItemSchema = recipeSummarySchema.extend({
    /** Only populated for availableIngredients searches. */
    missingIngredients: z.array(z.string()).optional(),
    matchScore: z.number().optional(),
});
export const recipePageSchema = cursorPageSchema(recipeListItemSchema);
// ---------------------------------------------------------------------------
// Scaling
// ---------------------------------------------------------------------------
export const scalePreviewRequestSchema = z.object({
    servings: positiveDecimalStringSchema,
    /** Convert into the user's preferred measurement system where a safe conversion exists. */
    convertUnits: z.boolean().default(false),
    /** Overrides the profile for this view (the "Original · Metric · US · UK" switch). */
    system: measurementSystemSchema.optional(),
});
export const scaledIngredientSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    note: z.string().nullable(),
    optional: z.boolean(),
    scalable: z.boolean(),
    quantity: decimalStringSchema.nullable(),
    quantityMax: decimalStringSchema.nullable(),
    quantityText: z.string().nullable(),
    unitCode: z.string().nullable(),
    unitText: z.string().nullable(),
    /** Human readable, locale aware text such as "1 ½ cups". */
    display: z.string(),
    /** Practical hint for count-based values, e.g. "use 1 egg + half a beaten egg". */
    hint: z.string().nullable(),
    converted: z.boolean(),
    /** A volume was turned into a weight (or back) through the ingredient's density: "ca.". */
    approximate: z.boolean().optional(),
});
export const scalePreviewResponseSchema = z.object({
    originalServings: decimalStringSchema,
    requestedServings: decimalStringSchema,
    factor: decimalStringSchema,
    sections: z.array(z.object({
        id: uuidSchema,
        title: z.string().nullable(),
        ingredients: z.array(scaledIngredientSchema),
    })),
});
// ---------------------------------------------------------------------------
// Duplicate / variation / revisions / sharing
// ---------------------------------------------------------------------------
export const duplicateRecipeRequestSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    asVariation: z.boolean().default(false),
    variationLabel: z.string().trim().max(80).optional(),
});
export const recipeRevisionSchema = z.object({
    id: uuidSchema,
    recipeId: uuidSchema,
    revision: z.number().int(),
    actor: publicUserSchema.nullable(),
    reason: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    summary: z.object({
        title: z.string(),
        ingredientCount: z.number().int(),
        stepCount: z.number().int(),
    }),
});
export const recipeRevisionDetailSchema = recipeRevisionSchema.extend({
    snapshot: recipeWriteInputSchema,
});
export const recipeDiffChangeSchema = z.object({
    path: z.string(),
    kind: z.enum(['added', 'removed', 'changed']),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
    label: z.string(),
});
export const recipeDiffSchema = z.object({
    changes: z.array(recipeDiffChangeSchema),
    ingredientsAdded: z.array(z.string()),
    ingredientsRemoved: z.array(z.string()),
    ingredientsChanged: z.array(z.object({ before: z.string(), after: z.string() })),
    stepsAdded: z.array(z.string()),
    stepsRemoved: z.array(z.string()),
    stepsChanged: z.array(z.object({ before: z.string(), after: z.string() })),
});
export const shareSettingsSchema = z.object({
    visibility: recipeVisibilitySchema,
    publicSlug: z.string().nullable(),
    shareUrl: z.string().nullable(),
});
export const updateShareRequestSchema = z.object({
    visibility: recipeVisibilitySchema,
    regenerateSlug: z.boolean().default(false),
});
/** Read-only public projection used for unlisted/public recipe pages. */
export const publicRecipeSchema = recipeSchema.omit({
    isFavorite: true,
    rating: true,
    notes: true,
    permissions: true,
    householdId: true,
    deletedAt: true,
    usedIn: true,
});
export const favoriteRequestSchema = z.object({ favorite: z.boolean() });
export const recipeVariationSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    variationLabel: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const trashActionSchema = z.enum(['restore', 'purge']);
//# sourceMappingURL=recipes.js.map